import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Loader, Home } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';
function getHeaders() { const t = localStorage.getItem('pkepl_token'); return t ? { Authorization: `Bearer ${t}` } : {}; }

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-semibold text-[#374151] font-sans uppercase tracking-wide">{label}</label>
    {children}
    {hint && <p className="text-xs text-[#9CA3AF] font-sans">{hint}</p>}
  </div>
);
const Input = (props) => (
  <input {...props} className="w-full px-3.5 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E5A57]/20 focus:border-[#0E5A57] font-sans bg-white text-[#1F2937]" />
);
const Textarea = (props) => (
  <textarea {...props} className="w-full px-3.5 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E5A57]/20 focus:border-[#0E5A57] font-sans bg-white text-[#1F2937] resize-none" />
);

export default function AdminHomepage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/homepage`, { withCredentials: true, headers: getHeaders() })
      .then(r => setSettings(r.data)).catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/homepage`, settings, { withCredentials: true, headers: getHeaders() });
      setSettings(data);
      toast.success('Homepage settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="flex items-center justify-center h-64"><Loader className="w-6 h-6 animate-spin text-[#0E5A57]" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-homepage-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-[#153E5C]">Homepage Settings</h1>
          <p className="text-sm text-[#6B7280] font-sans mt-1">Edit the hero section content shown on the homepage.</p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="homepage-save-btn"
          className="flex items-center gap-2 bg-[#0E5A57] hover:bg-[#2E7D7A] disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors">
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="space-y-6">
        {/* Hero */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-5">
          <h2 className="font-serif text-base font-bold text-[#153E5C] flex items-center gap-2">
            <Home className="w-4 h-4" /> Hero Section
          </h2>
          <Field label="Heading Line 1" hint="First line of the hero heading">
            <Input value={settings.hero_heading_line1 || ''} onChange={e => set('hero_heading_line1', e.target.value)} placeholder="Empowering Businesses." />
          </Field>
          <Field label="Heading Line 2 (Gold Colour)" hint="This line appears in gold/accent colour">
            <Input value={settings.hero_heading_line2 || ''} onChange={e => set('hero_heading_line2', e.target.value)} placeholder="Delivering Excellence." />
          </Field>
          <Field label="Hero Subheading">
            <Textarea value={settings.hero_subheading || ''} onChange={e => set('hero_subheading', e.target.value)} rows={3} placeholder="NKP VENTURES is a premium BPO partner..." />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Primary CTA Button Text">
              <Input value={settings.cta_primary_text || ''} onChange={e => set('cta_primary_text', e.target.value)} placeholder="Schedule a Consultation" />
            </Field>
            <Field label="Primary CTA Link">
              <Input value={settings.cta_primary_link || ''} onChange={e => set('cta_primary_link', e.target.value)} placeholder="/contact" />
            </Field>
            <Field label="Secondary CTA Button Text">
              <Input value={settings.cta_secondary_text || ''} onChange={e => set('cta_secondary_text', e.target.value)} placeholder="Explore Our Services" />
            </Field>
            <Field label="Secondary CTA Link">
              <Input value={settings.cta_secondary_link || ''} onChange={e => set('cta_secondary_link', e.target.value)} placeholder="/services" />
            </Field>
          </div>
        </div>

      </div>
    </div>
  );
}
